import React from "react";
import { PrivacyTermsModal } from "./PrivacyTermsModal";
import { StayConnectedDropdown } from "./StayConnectedDropdown";
import { MivaltaLandingModalProps } from "../../../lib/MivaltaLandingModalProps";
import PartnersSection from "./PartnersSection";
import logo from "../../../logo.png";

export default function DesktopLanding({ setModal }: MivaltaLandingModalProps) {
  const [openModal, setOpenModal] = React.useState<null | "privacy" | "terms">(
    null
  );

  return (
    <div
      className="min-h-screen bg-center bg-cover bg-no-repeat px-6 lg:px-12 pt-4 lg:pt-8 flex flex-col"
      style={{ backgroundImage: "url('/mivalta_background.png')" }}
    >
      <div className="w-full max-w-screen-xl text-white">
        <main className="flex-1 w-full max-w-screen-xl text-white self-center">
          {/* Header */}
          <header className="sticky top-0 z-50 bg-[rgba(35,200,161,0.08)] backdrop-blur-md border-b border-white/10 px-6 py-4 shadow-md flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src={logo} alt="MiValta logo" className="w-12 h-12" />
              <span className="text-3xl font-bold tracking-widest uppercase">
                MiValta
              </span>
            </div>
            <div className="text-base underline">
              <StayConnectedDropdown onSelectModal={setModal} />
            </div>
          </header>

          {/* Hero Section */}
          <section className="grid grid-cols-2 gap-16 items-center mt-24 mb-32">
            <div className="text-left">
              <h1 className="text-7xl font-extrabold leading-tight mb-6">
                Your Body.
                <br />
                Your Data.
              </h1>
              <p className="text-2xl mb-5">
                MiValta is the privacy-first activity & training platform where
                all your data comes together.
              </p>
              <p className="text-xl mb-5">
                Track and plan your training. Unlock AI-powered insights. Stay
                in full control and ownership of your data.
              </p>

              <p className="italic text-lg text-gray-300 mt-6">
                With MiValta, you don’t trade your data for features. You get
                both privacy and performance.
              </p>
            </div>

            <div className="flex flex-col items-center justify-center text-center px-8 py-5 w-full max-w-lg">
              <h2 className="text-8xl font-bold mb-4">MiValta</h2>
              <img
                src="/icons/private.png"
                alt="MiValta fingerprint logo"
                className="w-60 h-60"
              />
            </div>
          </section>

          <hr className="my-16 w-4/5 mx-auto border-t border-white/30" />

          {/* Why We’re Building MiValta */}
          <section className="grid grid-cols-2 gap-14 items-center mb-32">
            <div className="flex flex-col items-center justify-center text-center px-8 py-6">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-60 h-60 mb-6 text-white"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M17 8h-1V6a4 4 0 10-8 0v2H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V10a2 2 0 00-2-2zm-6 8a1 1 0 01-2 0v-2a1 1 0 112 0v2zm-2-8V6a2 2 0 114 0v2h-4z" />
              </svg>
              <h2 className="text-6xl font-bold mb-4">Data Privacy</h2>
            </div>
            <div className="space-y-6 pr-4 text-gray-200">
              <h2 className="text-5xl font-bold text-white mb-6">
                Why We’re Building MiValta
              </h2>
              <p className="text-xl font-bold leading-relaxed">
                Most fitness and activity platforms treat your data as their
                product.
              </p>
              <p className="text-xl leading-relaxed">
                No one should have to sacrifice privacy to use a training app.
              </p>

              <p className="italic text-lg text-gray-300 mt-6">
                Your movement is personal. Your progress is yours. We’re
                building the future of training — one that respects you.
              </p>

              <p className="italic text-lg text-gray-300 mt-6">
                Founded by an Olympic champion and high-performance coach.
              </p>
            </div>
          </section>

          <hr className="my-16 w-4/5 mx-auto border-t border-white/30" />

          {/* The MiValta Difference */}
          <section className="mb-32">
            <h2 className="text-4xl font-semibold text-center mb-14">
              The MiValta Difference
            </h2>
            <div className="grid grid-cols-3 gap-12">
              {[
                {
                  icon: "/icons/private.png",
                  title: "Private by Design",
                  desc: "Your data stays with you. No leaks. No third parties. No compromises.",
                },
                {
                  icon: "/icons/science.png",
                  title: "Science, Not Trends",
                  desc: "Built on evidence-based training — not hype, gimmicks, or fads.",
                },
                {
                  icon: "/icons/ai.png",
                  title: "Real Coaching Intelligence",
                  desc: "Our AI, Josi, adapts to your body, rhythm, and goals — running locally on your device.",
                },
              ].map(({ icon, title, desc }) => (
                <div key={title} className="text-center px-6">
                  <img
                    src={icon}
                    alt={title}
                    className="w-16 h-16 mb-4 mx-auto"
                  />
                  <h3 className="text-2xl font-bold mb-2">{title}</h3>
                  <p className="text-lg text-gray-200">{desc}</p>
                </div>
              ))}
            </div>
          </section>

          <hr className="my-16 w-4/5 mx-auto border-t border-white/30" />

          {/* Join Tester Section */}
          <section className="text-center mb-32 px-4">
            <h2 className="text-4xl font-bold mb-4">
              Be Part of the Foundation
            </h2>
            <p className="text-xl mb-4">
              We’re looking for cyclists and runners who care about performance
              and privacy.
            </p>
            <p className="text-lg mb-6 text-gray-200">
              Join our beta phase and help shape the future of MiValta.
            </p>
            <button
              className="bg-[#23c8a1] text-white text-base font-semibold uppercase tracking-wide px-8 py-3 rounded hover:brightness-110 transition"
              onClick={() => setModal("tester")}
            >
              Join as a Tester
            </button>
            <p className="italic text-base text-white mt-8">
              Be the first to train with AI that respects you.
            </p>
          </section>

          <hr className="my-16 w-4/5 mx-auto border-t border-white/30" />

          {/* Who It's For */}
          <section className="text-center mb-32">
            <h2 className="text-4xl font-bold mb-6">
              For People Who Move — On Their Terms
            </h2>
            <p className="text-xl max-w-4xl mx-auto mb-4">
              From seasoned competitors to lifelong movers; if you believe you
              shouldn’t have to sacrifice privacy to improve performance, you
              belong here.
            </p>
          </section>

          <hr className="my-16 w-4/5 mx-auto border-t border-white/30" />

          {/* Help Build Section */}
          <section className="gap-16 items-center mb-32 px-4 text-center">
            <div className="flex flex-col items-center">
              <h2 className="text-3xl font-bold mb-6">Help Build MiValta</h2>
              <p className="text-xl mb-6">
                Are you a developer, designer, creator, or supporter of ethical
                tech? We’re building something bigger — and we’d love to have
                you with us.
              </p>
              <button
                className="bg-[#23c8a1] text-white text-base font-semibold uppercase tracking-wide px-8 py-3 rounded hover:brightness-110 transition"
                onClick={() => setModal("build")}
              >
                Join MiValta
              </button>
            </div>
          </section>

          <hr className="my-16 w-4/5 mx-auto border-t border-white/30" />

          <PartnersSection />
        </main>
        {/* Footer */}
        <footer className="mt-auto text-center text-xs opacity-60">
          © 2025 MiValta. Ethics at its core. All rights reserved. |{" "}
          <button className="underline" onClick={() => setOpenModal("privacy")}>
            Privacy Promise
          </button>{" "}
          |{" "}
          <button className="underline" onClick={() => setOpenModal("terms")}>
            Terms
          </button>
        </footer>
      </div>

      <PrivacyTermsModal
        openModal={openModal}
        onClose={() => setOpenModal(null)}
      />
    </div>
  );
}
